import type { LocationRewrite } from '@tanstack/react-router';

/**
 * Path suffixes rendered as overlays from any deeper administration URL.
 * Matched routes stay on the base path via rewrite; the browser keeps the
 * full public URL.
 */
export const ADMINISTRATION_OVERLAY_SUFFIXES = [
  '/o/app-creator',
  '/o/first-app-trial',
  '/o/first-app',
  '/m/create',
  '/m/invite',
  '/m/rename',
  '/m/add-to-group',
  '/o/add-apps',
  '/o/add-users',
  '/o/add-external-id',
  '/o/edit-logo',
] as const;

export type AdministrationOverlaySuffix =
  (typeof ADMINISTRATION_OVERLAY_SUFFIXES)[number];

/** Internal search key — stripped again on output so it never appears publicly. */
export const ADMINISTRATION_OVERLAY_SEARCH_PARAM = '__adminOverlay';

const matchOverlaySuffix = (
  pathname: string,
): AdministrationOverlaySuffix | undefined => ADMINISTRATION_OVERLAY_SUFFIXES.find(
  suffix => pathname.endsWith(suffix),
);

export function parseAdministrationOverlay(
  pathname: string,
): AdministrationOverlaySuffix | null {
  return matchOverlaySuffix(pathname) ?? null;
}

export function isAdministrationOverlayPath(pathname: string): boolean {
  return matchOverlaySuffix(pathname) !== undefined;
}

export function isKnownAdministrationOverlayParam(value: string): boolean {
  return ADMINISTRATION_OVERLAY_SUFFIXES.includes(
    `/${value}` as AdministrationOverlaySuffix,
  );
}

/** `o/first-app` → `/o/first-app`, or null if unknown. */
function overlayFromParam(value: unknown): AdministrationOverlaySuffix | null {
  if (typeof value !== 'string' || !isKnownAdministrationOverlayParam(value)) {
    return null;
  }
  return `/${value}` as AdministrationOverlaySuffix;
}

/** Query string or full href that may contain `__adminOverlay`. */
function overlayFromQuery(source: string): AdministrationOverlaySuffix | null {
  const queryStart = source.indexOf('?');
  const query = queryStart === -1 ? source : source.slice(queryStart + 1);
  return overlayFromParam(
    new URLSearchParams(query.split('#')[0]).get(ADMINISTRATION_OVERLAY_SEARCH_PARAM),
  );
}

/** Overlay from parsed search, raw search string, or public path suffix. */
export function readAdministrationOverlay(location: {
  search?: Record<string, unknown>
  searchStr?: string
  href?: string
  publicHref?: string
}): AdministrationOverlaySuffix | null {
  const href = location.publicHref ?? location.href ?? '';

  return overlayFromParam(location.search?.[ADMINISTRATION_OVERLAY_SEARCH_PARAM])
    ?? overlayFromQuery(location.searchStr ?? '')
    ?? overlayFromQuery(href)
    ?? parseAdministrationOverlay(href.split(/[?#]/)[0]);
}

/**
 * Browser URL keeps `/…/m/rename` (etc.); the router matches the base path so
 * details/list chrome stay mounted under the overlay.
 *
 * Open overlays with `routerService.openAdministrationOverlay(...)`. Prefer
 * `router.pathname` in UI — after `input` that is already the base path.
 */
export const administrationOverlayRewrite: LocationRewrite = {
  // LocationRewrite mutates the provided URL in place by design.
  /* eslint-disable no-param-reassign */
  input: ({ url }) => {
    if (!url.pathname.startsWith('/administration/')) {
      url.searchParams.delete(ADMINISTRATION_OVERLAY_SEARCH_PARAM);
      return url;
    }

    const suffix = matchOverlaySuffix(url.pathname);
    if (suffix) {
      url.pathname = url.pathname.slice(0, -suffix.length) || '/';
      url.searchParams.set(ADMINISTRATION_OVERLAY_SEARCH_PARAM, suffix.slice(1));
      return url;
    }

    // Full-page load / history may already be the internal form
    // (`/administration/apps?__adminOverlay=o/first-app`). A second input
    // pass must keep a valid param — deleting it drops the overlay after
    // location.assign('/administration/apps/o/first-app').
    const existing = url.searchParams.get(ADMINISTRATION_OVERLAY_SEARCH_PARAM);
    if (existing && !isKnownAdministrationOverlayParam(existing)) {
      url.searchParams.delete(ADMINISTRATION_OVERLAY_SEARCH_PARAM);
    }

    return url;
  },
  output: ({ url }) => {
    const overlay = url.searchParams.get(ADMINISTRATION_OVERLAY_SEARCH_PARAM);
    if (overlay && url.pathname.startsWith('/administration/')) {
      url.searchParams.delete(ADMINISTRATION_OVERLAY_SEARCH_PARAM);
      if (!url.pathname.endsWith(`/${overlay}`)) {
        url.pathname = `${url.pathname.replace(/\/$/, '')}/${overlay}`;
      }
    }
    return url;
  },
  /* eslint-enable no-param-reassign */
};
